import React, { useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Typography,
  Button,
  Stack,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContentText,
  DialogContent,
  Chip,
} from "@mui/material";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteAnnouncement,
  getAnnouncementByIdCompany,
} from "../../../../actions/announcement";
import CreateAnnouncement from "./createAnnouncement";
import SnackbarResponse from "./snackbarResponse";

const WrapHeader = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  ["@media only screen and (max-width:600px)"]: {
    flexDirection: "column",
  },
  marginTop: 8,
});

const StyledAccordionSummary = styled((props) => (
  <AccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
    sx={{
      backgroundColor: props.isActive ? "#b2dfdb" : "#ff7961",
    }}
    {...props}
  />
))(({ theme }) => ({
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(3),
  },
}));

const AnnountAdmin = (props) => {
  const { updateAt, isActive, handleSelect, ...data } = props
  const { idAnnouncement, title, detail, file, startDate, endDate} = data
  
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);

  const handleOpenDelete = () => {
    setOpen(true);
  };

  const handleCloseDelete = () => {
    setOpen(false);
  };

  const handleDelete = async (idAnnouncement) => {
    const res = await dispatch(deleteAnnouncement(idAnnouncement));
    if (res) {
      handleCloseDelete();
      dispatch(getAnnouncementByIdCompany());
    }
  };

  return (
    <>
      <Accordion disableGutters>
        <StyledAccordionSummary isActive={isActive}>
          <Typography variant="h6">{title}</Typography>
        </StyledAccordionSummary>
        <AccordionDetails>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            flexWrap={"wrap"}
            marginBottom={1}
          >
            <Typography variant="subtitle1" color="text.secondary">
              {`วันที่ประกาศ: ${dayjs(startDate).format(
                "DD/MM/YYYY"
              )} - ${dayjs(endDate).format("DD/MM/YYYY")}`}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {`แก้ไขเมื่อ: ${dayjs(updateAt).format("DD/MM/YYYY HH:mm")}`}
            </Typography>
          </Stack>
          <Stack direction={"row"} alignItems={"center"} spacing={1}>
            <Chip
              icon={<DescriptionIcon />}
              label="รายละเอียด"
              style={{ marginRight: "auto" }}
            />
            <IconButton
              onClick={() => handleSelect(data)}
              style={{ color: "#ffeb3b", border: "1px solid #ffeb3b" }}
            >
              <EditIcon fontSize="inherit" />
            </IconButton>
            <IconButton
              style={{ color: "#d32f2f", border: "1px solid #d32f2f" }}
              onClick={handleOpenDelete}
            >
              <DeleteIcon fontSize="inherit" />
            </IconButton>
          </Stack>
          <Typography padding={1}>{detail}</Typography>
          {file && (
            <Button
              variant="outlined"
              component={Link}
              href={file}
              target="_blank"
              startIcon={<AttachFileIcon />}
              sx={{ justifyContent: "flex-end" }}
            >
              ไฟล์แนบประกาศ
            </Button>
          )}
        </AccordionDetails>
      </Accordion>
      {open && (
        <Dialog open={open}>
          <DialogTitle>ยืนยันลบประกาศ</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <Typography>{title}</Typography>
              ประกาศจะถูกลบอย่างถาวร
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDelete(idAnnouncement)}
            >
              ยืนยัน
            </Button>
            <Button onClick={handleCloseDelete}>ยกเลิก</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default function Announcement() {
  const dispatch = useDispatch();
  const { result: userProfile } = useSelector((state) => state.userProfile);
  const { result: announcement } = useSelector((state) => state.announcement);

  const [selectAnnouncement, setSelectAnnouncement] = useState(null);
  const [open, setOpen] = useState(false);
  const [configSnackbar, setConfigSnackbar] = useState({
    open: false,
    message: "",
    color: "primary",
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSelect = (value) => {
    setSelectAnnouncement(value);
    setOpen(true);
  };

  const handleCloseSnackbar = () => {
    setConfigSnackbar({ open: false, message: "", color: "primary" });
  };

  const announcementActive = useMemo(() => {
    if (announcement) {
      return announcement.filter((item) =>
        dayjs().isBetween(item.startDate, item.endDate, "day", "[]")
      );
    }
  }, [announcement]);

  const announcementTotal = useMemo(() => {
    if (announcement) {
      return announcement.filter(
        (item) => !dayjs().isBetween(item.startDate, item.endDate, "day", "[]")
      );
    }
  }, [announcement]);

  return (
    <>
      <WrapHeader>
        <Typography variant="h4">การประกาศ</Typography>
        {userProfile && userProfile.roles.includes("ROLE_ADMIN") && (
          <Button variant="contained" color="primary" onClick={handleClickOpen}>
            สร้างประกาศ
          </Button>
        )}
      </WrapHeader>
      {open &&
        <CreateAnnouncement  
          open={open} 
          handleClose={handleClose} 
          editData={selectAnnouncement}
          setConfigSnackbar={setConfigSnackbar} 
        />
      }
      <Stack marginTop={4}>
        <Typography marginBottom={2}>ดำเนินการอยู่</Typography>
        {announcementActive &&
          announcementActive.map((item) => (
            <AnnountAdmin
              key={item.idPolicy}
              handleSelect={handleSelect}
              isActive={true}
              {...item}
            />
          ))}
      </Stack>
      <Stack marginTop={4} marginBottom={4}>
        <Typography marginBottom={2}>ทั้งหมด</Typography>
        {announcementTotal &&
          announcementTotal.map((item) => (
            <AnnountAdmin
              key={item.idAnnouncement}
              handleSelect={handleSelect}
              {...item}
              isActive={false}
            />
          ))}
      </Stack>
      {configSnackbar.open && (
        <SnackbarResponse
          configSnackbar={configSnackbar}
          handleClose={handleCloseSnackbar}
        />
      )}
    </>
  );
}
